// @ts-nocheck
import _ from 'lodash';
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import omitEmpty from 'omit-empty';
import {
  makeStyles,
  Box,
  Grid,
  Icon,
  TextField,
  Button,
  Typography,
  MenuItem,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
} from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { Controller, useForm } from 'react-hook-form';
import { useIfMounted } from '../../../../common/hooks';

const useStyles = makeStyles(theme => ({
  container: {
    flex: '1',
    display: 'flex',
  },
  form: {
    flex: '1',
    display: 'flex',
    flexDirection: 'column',
    '& .MuiFormControl-root': {
      minHeight: '64px',
      '& .MuiFormHelperText-marginDense': {
        marginTop: '2px',
      },
    },
  },
  formContent: {
    flex: '1',
    display: 'flex',
    flexWrap: 'wrap',
    padding: theme.spacing(1, 3),
    marginBottom: theme.spacing(1),
    overflow: 'auto',
  },
  formSection: {
    flex: 1,
    '&:first-child': {
      marginRight: theme.spacing(3),
    },
    '&:last-child': {
      flex: '1 1 100%',
    },
    '& .MuiTypography-gutterBottom': {
      marginBottom: '1em',
    },
    '& .MuiGrid-item': {
      padding: theme.spacing(0, 1),
    },
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    padding: theme.spacing(1),
  },
  button: {
    margin: theme.spacing(1),
    '&:last-child': {
      marginRight: '0',
    },
  },
}));

function CompanyForm(props) {
  const {
    company,
    onSave,
    onCancel,
    editMode,
    actionsDisabled,
    cancelButtonText,
    cancelButtonIcon,
    saveButtonText,
    saveButtonIcon,
  } = props;

  const [editable, setEditable] = useState(!editMode);
  const [t] = useTranslation();
  const { formState, errors, control, reset, register, handleSubmit } = useForm();
  const classes = useStyles();
  const ifMounted = useIfMounted();

  const { isDirty, isSubmitting } = formState;

  const onSubmit = async data => {
    const formData = omitEmpty(data);
    if (company?.id) {
      formData.id = company.id;
    }
    try {
      await onSave(formData);
      ifMounted(() => {
        reset(data);
        if (editMode) {
          setEditable(false);
        }
      });
    } catch (e) {
      // Error already handled
    }
  };

  const getStatusValue = status => {
    switch (status) {
      case t('company.companyStatusActive'):
        return 'ACTIVE';
      case t('company.companyStatusExpired'):
        return 'EXPIRED';
      default:
        return 'ACTIVE';
    }
  };

  const getCountryValue = () => {
    if (company?.country && company.country !== '') return company.country;
    return 'DK';
  };

  const handleCancel = () => {
    if (editMode && editable) {
      reset();
      setEditable(false);
    } else {
      onCancel();
    }
  };

  return (
    <Box id="CompanyForm-container" className={classes.container}>
      <form id="CompanyForm-form" className={classes.form} onSubmit={handleSubmit(onSubmit)}>
        <Box className={classes.formContent}>
          <Box className={classes.formSection}>
            <Typography variant="h6" gutterBottom>
              {t('company.companyDetailsSection')}
            </Typography>
            <Grid container spacing={2}>
              {company?.id && (
                <Grid item xs={12}>
                  <TextField
                    id="CompanyForm-id"
                    name="id"
                    label={t('company.companyId')}
                    variant="outlined"
                    size="small"
                    defaultValue={company?.id}
                    InputLabelProps={{ shrink: editMode }}
                    inputRef={register}
                    disabled
                    fullWidth
                  />
                </Grid>
              )}
              <Grid item xs={12}>
                <TextField
                  id="CompanyForm-name"
                  name="name"
                  label={t('company.companyName')}
                  variant="outlined"
                  size="small"
                  defaultValue={company?.name}
                  InputLabelProps={{ shrink: editMode }}
                  inputRef={register({
                    required: t('company.companyNameRequired'),
                  })}
                  error={!!errors.name}
                  helperText={errors.name?.message}
                  disabled={!editable}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  id="CompanyForm-cvrNumber"
                  name="cvrNumber"
                  label={t('company.companyCvrNumber')}
                  variant="outlined"
                  size="small"
                  defaultValue={company?.cvrNumber ? company.cvrNumber : undefined}
                  InputLabelProps={{ shrink: editMode }}
                  inputRef={register({
                    pattern: {
                      value: /^\S{8}$/,
                      message: t('company.companyCvrNumberInvalid'),
                    },
                  })}
                  error={!!errors.cvrNumber}
                  helperText={errors.cvrNumber?.message}
                  disabled={!editable}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  id="CompanyForm-eanNumber"
                  name="eanNumber"
                  label={t('company.companyEanNumber')}
                  variant="outlined"
                  size="small"
                  defaultValue={company?.eanNumber}
                  InputLabelProps={{ shrink: editMode }}
                  inputRef={register({
                    pattern: {
                      value: /^\S{13}$/,
                      message: t('company.companyEanNumberInvalid'),
                    },
                  })}
                  error={!!errors.eanNumber}
                  helperText={errors.eanNumber?.message}
                  disabled={!editable}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12}>
                <Controller
                  name="status"
                  control={control}
                  defaultValue={getStatusValue(company?.status)}
                  as={
                    <TextField
                      id="CompanyForm-status"
                      label={t('company.companyStatus')}
                      variant="outlined"
                      size="small"
                      disabled={!editable}
                      fullWidth
                      select
                    >
                      <MenuItem value="ACTIVE">{t('company.companyStatusActive')}</MenuItem>
                      <MenuItem value="EXPIRED">{t('company.companyStatusExpired')}</MenuItem>
                    </TextField>
                  }
                />
              </Grid>
              <Grid item xs={4}>
                <FormControl component="fieldset">
                  <FormLabel component="legend">{t('company.companySegment')}</FormLabel>
                  <Controller
                    name="segment"
                    control={control}
                    defaultValue={_.defaultTo(company?.segment, 'B2B')}
                    as={
                      <RadioGroup id="CompanyForm-type">
                        <FormControlLabel
                          value="B2B"
                          control={<Radio />}
                          label={t('company.companySegmentB2B')}
                          disabled={!editable || editMode}
                        />
                        <FormControlLabel
                          value="TV2REGION"
                          control={<Radio />}
                          label={t('company.companySegmentRegion')}
                          disabled={!editable || editMode}
                        />
                      </RadioGroup>
                    }
                  />
                </FormControl>
              </Grid>
            </Grid>
          </Box>
          <Box className={classes.formSection}>
            <Typography variant="h6" gutterBottom>
              {t('company.companyAddressSection')}
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  id="CompanyForm-department"
                  name="department"
                  label={t('company.companyDepartment')}
                  variant="outlined"
                  size="small"
                  defaultValue={company?.department}
                  InputLabelProps={{ shrink: editMode }}
                  inputRef={register}
                  disabled={!editable}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  id="CompanyForm-address"
                  name="address"
                  label={t('company.companyAddress')}
                  variant="outlined"
                  size="small"
                  defaultValue={company?.address}
                  InputLabelProps={{ shrink: editMode }}
                  inputRef={register({
                    required: t('company.companyAddressRequired'),
                  })}
                  error={!!errors.address}
                  helperText={errors.address?.message}
                  disabled={!editable}
                  fullWidth
                />
              </Grid>
              <Grid item xs={5}>
                <TextField
                  id="CompanyForm-zipcode"
                  name="zipcode"
                  label={t('company.companyZipcode')}
                  variant="outlined"
                  size="small"
                  defaultValue={company?.zipcode}
                  InputLabelProps={{ shrink: editMode }}
                  inputRef={register({
                    required: t('company.companyZipcodeRequired'),
                  })}
                  error={!!errors.zipcode}
                  helperText={errors.zipcode?.message}
                  disabled={!editable}
                  fullWidth
                />
              </Grid>
              <Grid item xs={7}>
                <TextField
                  id="CompanyForm-city"
                  name="city"
                  label={t('company.companyCity')}
                  variant="outlined"
                  size="small"
                  defaultValue={company?.city}
                  InputLabelProps={{ shrink: editMode }}
                  inputRef={register({
                    required: t('company.companyCityRequired'),
                  })}
                  error={!!errors.city}
                  helperText={errors.city?.message}
                  disabled={!editable}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12}>
                <Controller
                  name="country"
                  control={control}
                  defaultValue={getCountryValue}
                  as={
                    <TextField
                      id="CompanyForm-country"
                      label={t('company.companyCountry')}
                      variant="outlined"
                      size="small"
                      disabled={!editable}
                      fullWidth
                      select
                    >
                      <MenuItem value="DK">DK</MenuItem>
                      <MenuItem value="NO">NO</MenuItem>
                      <MenuItem value="SE">SE</MenuItem>
                    </TextField>
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  id="CompanyForm-phone"
                  name="phone"
                  label={t('company.companyPhone')}
                  variant="outlined"
                  size="small"
                  defaultValue={company?.phone}
                  InputLabelProps={{ shrink: editMode }}
                  inputRef={register}
                  disabled={!editable}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  id="CompanyForm-invoiceEmail"
                  name="invoiceEmail"
                  label={t('company.companyInvoiceEmail')}
                  variant="outlined"
                  size="small"
                  defaultValue={company?.invoiceEmail}
                  InputLabelProps={{ shrink: editMode }}
                  inputRef={register({
                    pattern: {
                      value: /^\w+([-+.']\w+)*@\w+([-.]\w+)*\.\w+([-.]\w+)*$/,
                      message: t('company.companyEmailInvalid'),
                    },
                  })}
                  error={!!errors.invoiceEmail}
                  helperText={errors.invoiceEmail?.message}
                  disabled={!editable}
                  fullWidth
                />
              </Grid>
            </Grid>
          </Box>
          <Box className={classes.formSection}>
            <Typography variant="h6" gutterBottom>
              {t('company.companyContactSection')}
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  id="CompanyForm-contactName"
                  name="contactName"
                  label={t('company.companyContactName')}
                  variant="outlined"
                  size="small"
                  defaultValue={company?.contactName}
                  InputLabelProps={{ shrink: editMode }}
                  inputRef={register({
                    required: t('company.companyContactNameRequired'),
                  })}
                  error={!!errors.contactName}
                  helperText={errors.contactName?.message}
                  disabled={!editable}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  id="CompanyForm-contactPhone"
                  name="contactPhone"
                  label={t('company.companyContactPhone')}
                  variant="outlined"
                  size="small"
                  defaultValue={company?.contactPhone}
                  InputLabelProps={{ shrink: editMode }}
                  inputRef={register}
                  disabled={!editable}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  id="CompanyForm-contactEmail"
                  name="contactEmail"
                  label={t('company.companyContactEmail')}
                  variant="outlined"
                  size="small"
                  defaultValue={company?.contactEmail}
                  InputLabelProps={{ shrink: editMode }}
                  inputRef={register({
                    pattern: {
                      value: /^\w+([-+.']\w+)*@\w+([-.]\w+)*\.\w+([-.]\w+)*$/,
                      message: t('company.companyEmailInvalid'),
                    },
                  })}
                  error={!!errors.contactEmail}
                  helperText={errors.contactEmail?.message}
                  disabled={!editable}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  id="CompanyForm-notes"
                  name="notes"
                  label={t('company.companyNotes')}
                  variant="outlined"
                  size="small"
                  defaultValue={company?.notes}
                  InputLabelProps={{ shrink: editMode }}
                  inputRef={register}
                  disabled={!editable}
                  rows={4}
                  multiline
                  fullWidth
                />
              </Grid>
            </Grid>
          </Box>
        </Box>
        <Box className={classes.actions}>
          <Button
            id="CompanyForm-cancel"
            className={classes.button}
            startIcon={<Icon>{!editable ? cancelButtonIcon : 'cancel'}</Icon>}
            onClick={handleCancel}
            disabled={actionsDisabled || isSubmitting}
          >
            <Typography variant="button" noWrap>
              {!editable ? cancelButtonText : t('common.cancel')}
            </Typography>
          </Button>
          {!editable ? (
            <Button
              id="CompanyForm-edit"
              key="edit"
              color="primary"
              className={classes.button}
              startIcon={<Icon>edit</Icon>}
              onClick={() => setEditable(true)}
              disabled={actionsDisabled || isSubmitting}
            >
              <Typography variant="button" noWrap>
                {t('common.edit')}
              </Typography>
            </Button>
          ) : (
            <Button
              id="CompanyForm-save"
              key="save"
              type="submit"
              color="primary"
              className={classes.button}
              startIcon={<Icon>{_.defaultTo(saveButtonIcon, 'save')}</Icon>}
              disabled={actionsDisabled || isSubmitting || !isDirty}
            >
              <Typography variant="button" noWrap>
                {_.defaultTo(saveButtonText, t('common.save'))}
              </Typography>
            </Button>
          )}
        </Box>
      </form>
    </Box>
  );
}

CompanyForm.defaultProps = {
  company: undefined,
  cancelButtonText: undefined,
  cancelButtonIcon: undefined,
  saveButtonText: undefined,
  saveButtonIcon: undefined,
  actionsDisabled: undefined,
  editMode: undefined,
};

CompanyForm.propTypes = {
  company: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    cvrNumber: PropTypes.number,
    eanNumber: PropTypes.string,
    status: PropTypes.string,
    segment: PropTypes.string,
    department: PropTypes.string,
    address: PropTypes.string,
    zipcode: PropTypes.string,
    city: PropTypes.string,
    country: PropTypes.string,
    phone: PropTypes.string,
    invoiceEmail: PropTypes.string,
    contactName: PropTypes.string,
    contactPhone: PropTypes.string,
    contactEmail: PropTypes.string,
    notes: PropTypes.string,
  }),
  cancelButtonText: PropTypes.string,
  cancelButtonIcon: PropTypes.string,
  saveButtonText: PropTypes.string,
  saveButtonIcon: PropTypes.string,
  onSave: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  actionsDisabled: PropTypes.bool,
  editMode: PropTypes.bool,
};

export default CompanyForm;
